import React, { forwardRef, useCallback } from 'react';

import { cn } from '@lyearn/style';
import MediaGrid from '@/components/LegacyMediaContainer/Grid';
import useMediaList from '@/components/LegacyMediaContainer/hooks/useMediaList';
import MediaList from '@/components/LegacyMediaContainer/List';
import { EntityMediaFragment } from '@/features/messages/graphql/fragments/generated/MediaType';

import MediaListContainer from '../Media/components/MediaListContainer';
import { MediaListContainerProps } from '../Media/components/MediaListContainer/types';
import { MediaContainerProps, MediaDisplay } from './types';

export type MediaContainerRef = {
  onClick: (media: EntityMediaFragment) => void;
};

const MediaContainer = forwardRef<MediaContainerRef, MediaContainerProps>(
  (
    {
      medias = [],
      display,
      size,
      className,
      pdfClassName,
      mediaActions,
      onMediaAction,
      isDownloadable,
      user,
      autoPlay,
      openMediaPlayerOnLoad,
      onMediaPlayerToggle,
      audioClassName,
      videoClassName,
      imageClassName,
      renderWithOverlay,
      actionListClassName,
      onMediaProgress,
      onClose,
      allowForwarding,
      allowPlaybackSpeedControl,
      initialProgress,
      allowBackgroundVideoPlayback,
    },
    ref,
  ) => {
    const { defaultDisplay, actions, onAction } = useMediaList(medias, mediaActions, onMediaAction);

    const displayMode = display ? display : defaultDisplay;
    const MediaComp = displayMode === MediaDisplay.GRID ? MediaGrid : MediaList;

    const renderContent = useCallback(
      (
        media: EntityMediaFragment,
        index: number,
        arr: EntityMediaFragment[],
        onClick: (media: EntityMediaFragment) => void,
      ) => {
        const content = (
          <MediaComp
            key={media.mediaId}
            actionListClassName={actionListClassName}
            actions={actions}
            audioClassName={audioClassName}
            className={cn(className, displayMode)}
            imageClassName={imageClassName}
            index={index}
            media={media}
            pdfClassName={pdfClassName}
            size={size}
            totalMedias={arr.length}
            user={user}
            videoClassName={videoClassName}
            onAction={onAction}
            onClick={onClick}
          />
        );
        if (renderWithOverlay)
          return renderWithOverlay({
            media,
            children: content,
            containerClassName: cn({ 'mt-4': arr.length > 1 }),
          });

        return content;
      },
      [
        MediaComp,
        actionListClassName,
        actions,
        audioClassName,
        className,
        displayMode,
        imageClassName,
        onAction,
        pdfClassName,
        renderWithOverlay,
        size,
        user,
        videoClassName,
      ],
    );

    const renderList: MediaListContainerProps['renderList'] = useCallback(
      ({ medias, onClick }) => {
        return medias.map((media, index) => renderContent(media, index, medias, onClick));
      },
      [renderContent],
    );

    if (!medias?.length) {
      return null;
    }

    return (
      <MediaListContainer
        allowBackgroundVideoPlayback={allowBackgroundVideoPlayback}
        allowForwarding={allowForwarding}
        allowPlaybackSpeedControl={allowPlaybackSpeedControl}
        autoPlay={autoPlay}
        initialProgress={initialProgress}
        isDownloadable={isDownloadable}
        medias={medias}
        openMediaPlayerOnLoad={openMediaPlayerOnLoad}
        renderList={renderList}
        onClose={onClose}
        onMediaPlayerToggle={onMediaPlayerToggle}
        onMediaProgress={onMediaProgress}
      />
    );
  },
);

export default React.memo(MediaContainer);
